import React, { useEffect, useMemo, useRef, useState } from 'react';
import styles from '../../styles/components/sections/TrustedBySection.module.css';
import { OptimizedImage } from '../common';

// A marquee needs at least one clone of the logo strip to loop seamlessly, and
// enough clones to cover the viewport when there are only a handful of logos.
const MIN_SETS = 2;
const MAX_SETS = 12;
const SCROLL_SPEED_DESKTOP = 75; // px per second
const SCROLL_SPEED_MOBILE = 55;

/**
 * A reusable component that displays trusted by logos with scrolling animation
 *
 * @param {Object} props
 * @param {Object} props.data - The trusted by data from Strapi
 * @param {String} props.data.title - Section title
 * @param {Array} props.data.trusted_customers - Array of customer logo objects
 */
const TrustedBySection = ({ data }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isClient, setIsClient] = useState(false);
  const [layout, setLayout] = useState({ setWidth: 0, repeatCount: MIN_SETS });

  const containerRef = useRef(null);
  const setRef = useRef(null);

  // Filter logos that have valid URLs
  const validLogos = useMemo(
    () => data?.trusted_customers?.filter(item => item?.logo?.url) || [],
    [data]
  );

  // Set client-side flag
  useEffect(() => {
      setIsClient(true);
  }, []);

  // Detect system dark mode preference
  useEffect(() => {
      if (!isClient) return;

      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      setIsDarkMode(mediaQuery.matches);

      const handleChange = e => {
          setIsDarkMode(e.matches);
          updateTheme(e.matches ? 'dark' : 'light');
      };

      mediaQuery.addEventListener('change', handleChange);

      // Check for saved theme preference
      const savedTheme = localStorage.getItem('theme');
      if (savedTheme) {
          setIsDarkMode(savedTheme === 'dark');
          updateTheme(savedTheme);
      } else {
          // Set initial theme based on system preference
          updateTheme(mediaQuery.matches ? 'dark' : 'light');
      }

      return () => mediaQuery.removeEventListener('change', handleChange);
  }, [isClient]);

  // Detect mobile device
  useEffect(() => {
      if (!isClient) return;

      const checkMobile = () => {
          setIsMobile(window.innerWidth <= 768);
      };

      checkMobile();
      window.addEventListener('resize', checkMobile);

      return () => window.removeEventListener('resize', checkMobile);
  }, [isClient]);

  // Measure one logo strip so the animation can travel exactly its width and so
  // we only clone as many strips as are needed to cover the container.
  useEffect(() => {
      if (!isClient || validLogos.length === 0) return;

      const containerEl = containerRef.current;
      const setEl = setRef.current;
      if (!containerEl || !setEl) return;

      const measure = () => {
          const setWidth = Math.round(setEl.getBoundingClientRect().width);
          const containerWidth = containerEl.getBoundingClientRect().width;
          if (setWidth <= 0 || containerWidth <= 0) return;

          const repeatCount = Math.min(
              MAX_SETS,
              Math.max(MIN_SETS, Math.ceil(containerWidth / setWidth) + 1)
          );

          setLayout(prev =>
              prev.setWidth === setWidth && prev.repeatCount === repeatCount
                  ? prev
                  : { setWidth, repeatCount }
          );
      };

      measure();

      if (typeof ResizeObserver === 'undefined') return;

      const observer = new ResizeObserver(measure);
      observer.observe(containerEl);
      observer.observe(setEl);

      return () => observer.disconnect();
  }, [isClient, validLogos.length]);

  // Update theme on document
  const updateTheme = theme => {
      document.documentElement.setAttribute('data-theme', theme);
      localStorage.setItem('theme', theme);
  };

  if (!data || validLogos.length === 0) return null;

  const trackStyle = layout.setWidth > 0
    ? {
        '--tb-scroll-distance': `${layout.setWidth}px`,
        '--tb-scroll-duration': `${(
            layout.setWidth / (isMobile ? SCROLL_SPEED_MOBILE : SCROLL_SPEED_DESKTOP)
        ).toFixed(2)}s`,
      }
    : undefined;

  // Only the first strip carries alt text. Every clone exists purely for the
  // looping animation, so it is hidden from crawlers and assistive tech to
  // avoid repeating the same brand names throughout the markup.
  const renderSet = (setIndex) => (
    <div
      key={setIndex}
      className={styles['scroll-set']}
      ref={setIndex === 0 ? setRef : undefined}
      aria-hidden={setIndex === 0 ? undefined : 'true'}
    >
      {validLogos.map((item, index) => (
        <div key={`${setIndex}-${item.id ?? item.logo.url ?? index}`} className={styles['scroll-item']}>
          <div className={`${styles['tb-card']} ${isDarkMode ? styles['dark-card'] : ''}`}>
            <OptimizedImage
              src={item.logo.url}
              alt={setIndex === 0 ? (item.logo.alternativeText || item.name || '') : ''}
              className={styles['img-settings']}
              width={84}
              height={80}
              style={{ objectFit: 'contain', width: '100%' }}
              priority={false}
              loading="lazy"
              quality={40}
              sizes="100px"
            />
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <section className={`py-6 ${isDarkMode ? styles['dark-section'] : ''}`} style={{ background: isDarkMode ? 'var(--bg-primary)' : '#fff' }}>
      <div className="tw-container">
        <div className="row justify-content-center">
          <p className={`text-center ${styles['tb-heading']} ${isDarkMode ? styles['dark-heading'] : ''}`}>{data?.title}</p>
          <div className="row pt-5 justify-content-md-center">
            <div className={styles['scroll-container']} ref={containerRef}>
              <div className={styles['scroll-content']} style={trackStyle}>
                {Array.from({ length: layout.repeatCount }, (_, setIndex) => renderSet(setIndex))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustedBySection;
